import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  ArrowRight, 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin,
  Clock,
  Palette,
  Beaker,
  BookOpen,
  Home,
  Coffee,
  Gift
} from 'lucide-react';

const UpdatedFooter = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  // Main Product Categories
  const productLinks = [
    { name: 'Resin Dining Tables', href: '/products?category=dining' },
    { name: 'Resin Coffee Tables', href: '/products?category=coffee' },
    { name: 'Resin Console Tables', href: '/products?category=console' },
    { name: 'Resin Side Tables', href: '/products?category=accent' },
    { name: 'Epoxy Resin Bar Tops', href: '/products?category=bar' },
    { name: 'Resin Office Furniture', href: '/products?category=office' },
    { name: 'Custom Resin Tables', href: '/contact' },
    { name: 'Buy Resin Table Online', href: '/products' },
  ];

  // Home Decor Products
  const homeDecorLinks = [
    { name: 'Resin Clocks', href: '/home-decor', icon: Clock },
    { name: 'Epoxy Resin Clocks', href: '/home-decor', icon: Clock },
    { name: 'Wooden Resin Clocks', href: '/home-decor', icon: Clock },
    { name: 'Handmade Resin Trays', href: '/home-decor', icon: Gift },
    { name: 'Epoxy Resin Trays', href: '/home-decor', icon: Gift },
    { name: 'Resin Coasters', href: '/home-decor', icon: Coffee },
    { name: 'Wooden Resin Coasters', href: '/home-decor', icon: Coffee },
  ];

  // Art & Decor Categories
  const artDecorLinks = [
    { name: 'Epoxy Resin Wall Decor', href: '/home-decor', icon: Palette },
    { name: '3D Wall Resin Painting', href: '/home-decor', icon: Palette },
    { name: '3D Wall Decor', href: '/home-decor', icon: Palette },
    { name: 'Resin Geode Art', href: '/home-decor', icon: Palette },
    { name: 'Resin Blue Sea Art', href: '/home-decor', icon: Palette },
    { name: 'Resin Panels', href: '/home-decor', icon: Palette },
  ];

  // Education & Learning
  const educationLinks = [
    { name: 'Resin Art Workshop in Udaipur', href: '/blog', icon: BookOpen },
    { name: 'Online Resin Art Workshop', href: '/blog', icon: BookOpen },
    { name: 'Best Resin Art Workshop', href: '/blog', icon: BookOpen },
    { name: 'Resin Art Classes in Udaipur', href: '/blog', icon: BookOpen },
    { name: 'Resin Art in India', href: '/blog', icon: BookOpen },
    { name: 'Resin Artwork Shop in Ahmedabad', href: '/blog', icon: BookOpen },
    { name: 'Learn Resin Art', href: '/blog', icon: BookOpen },
  ];

  // Formulas & Techniques
  const formulaLinks = [
    { name: 'Resin Formula', href: '/resin-formulas', icon: Beaker },
    { name: 'Resin Formula Preservation', href: '/resin-formulas', icon: Beaker },
    { name: 'Resin Flower Preservation', href: '/resin-formulas', icon: Beaker },
    { name: 'Advanced Resin Techniques', href: '/resin-formulas', icon: Beaker },
  ];

  const serviceLinks = [
    { name: 'Custom Commissions', href: '/contact' },
    { name: 'Design Consultation', href: '/about' },
    { name: 'Installation Services', href: '/about' },
    { name: 'Maintenance Plans', href: '/about' },
    { name: 'Workshops & Classes', href: '/blog' },
    { name: 'Restoration Services', href: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/about' },
    { name: 'Terms of Service', href: '/about' },
    { name: 'Shipping Policy', href: '/about' },
    { name: 'Return Policy', href: '/about' },
    { name: 'Warranty Information', href: '/about' },
  ];

  return (
    <footer className="bg-dark-900 border-t border-dark-800">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
                  <span className="text-xl font-bold text-white">RC</span>
                </div>
                <div>
                  <span className="text-xl font-bold bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent font-serif">
                    ResinCraft
                  </span>
                  <div className="text-sm text-dark-400 -mt-1">Studio</div>
                </div>
              </Link>
              
              <p className="text-dark-300 mb-6 leading-relaxed">
                Creating exceptional resin furniture and art pieces that transform spaces and inspire generations. 
                Every piece is handcrafted with passion and precision in India.
              </p>
              
              <div className="flex space-x-4 mb-6">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 bg-dark-800 hover:bg-accent-600 rounded-full flex items-center justify-center transition-colors duration-300 group"
                  >
                    <social.icon className="h-5 w-5 text-dark-400 group-hover:text-white" />
                  </a>
                ))}
              </div>

              {/* Newsletter */}
              <div>
                <h4 className="font-medium text-white mb-3">Stay Updated</h4>
                <div className="flex">
                  <input 
                    type="email" 
                    placeholder="Your email" 
                    className="flex-1 px-4 py-2 bg-dark-800 border border-dark-700 rounded-l-lg focus:border-accent-500 focus:outline-none text-sm text-white placeholder-dark-400"
                  />
                  <button className="bg-gradient-to-r from-accent-500 to-accent-600 px-4 py-2 rounded-r-lg hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                    <ArrowRight className="h-4 w-4 text-white" />
                  </button>
                </div>
                <p className="text-xs text-dark-400 mt-2">
                  Get updates on new products, workshops, and exclusive offers
                </p>
              </div>
            </div>

            {/* Main Products */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <Home className="h-5 w-5 mr-2 text-accent-400" />
                Resin Tables
              </h3>
              <ul className="space-y-3">
                {productLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-accent-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Home Decor */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-accent-400" />
                Home Decor
              </h3>
              <ul className="space-y-3">
                {homeDecorLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-accent-400 transition-colors duration-300 text-sm flex items-center"
                    >
                      {link.icon && <link.icon className="h-4 w-4 mr-2 text-accent-400" />}
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Art & Education */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <Palette className="h-5 w-5 mr-2 text-accent-400" />
                Art & Learning
              </h3>
              <ul className="space-y-3">
                {artDecorLinks.slice(0, 4).map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-accent-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
                {educationLinks.slice(0, 3).map((link, index) => (
                  <li key={index + 4}>
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-accent-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services & Support */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-6">Services & Support</h3>
              <ul className="space-y-3">
                {serviceLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-accent-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Formulas Section */}
              <div className="mt-6">
                <h4 className="font-medium text-white mb-3 flex items-center">
                  <Beaker className="h-4 w-4 mr-2 text-accent-400" />
                  Formulas
                </h4>
                <ul className="space-y-2">
                  {formulaLinks.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.href}
                        className="text-dark-300 hover:text-accent-400 transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Categories Section */}
        <div className="border-t border-dark-800 py-12">
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-white mb-2">
              Popular Categories
            </h3>
            <p className="text-dark-400 text-sm">
              Explore our most sought-after resin art collections
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { name: 'River Tables', href: '/products?category=dining', icon: '🏞️' },
              { name: 'Ocean Art', href: '/home-decor', icon: '🌊' },
              { name: 'Galaxy Clocks', href: '/home-decor', icon: '⭐' },
              { name: 'Geode Coasters', href: '/home-decor', icon: '💎' },
              { name: 'Wall Art', href: '/home-decor', icon: '🎨' },
              { name: 'Workshops', href: '/blog', icon: '🎓' },
            ].map((category, index) => (
              <Link
                key={index}
                to={category.href}
                className="bg-dark-800/50 rounded-xl p-4 text-center hover:bg-dark-700/50 transition-all duration-300 hover:scale-105"
              >
                <div className="text-2xl mb-2">{category.icon}</div>
                <div className="text-sm font-medium text-white">{category.name}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-dark-800 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start space-x-3">
              <Phone className="h-5 w-5 text-accent-400 mt-0.5" />
              <div>
                <div className="text-white font-medium">+91 12345 67890</div>
                <div className="text-dark-400 text-sm">Mon-Fri 9AM-6PM IST</div>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-accent-400 mt-0.5" />
              <div>
                <div className="text-white font-medium">info@resincraftstudio.com</div>
                <div className="text-dark-400 text-sm">We'll respond within 24hrs</div>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-accent-400 mt-0.5" />
              <div>
                <div className="text-white font-medium">Udaipur, Rajasthan</div>
                <div className="text-dark-400 text-sm">India - 313001</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-dark-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-dark-400 text-sm">
              © {currentYear} ResinCraft Studio. All rights reserved. | Made with ❤️ in India
            </div>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-dark-400">
              {legalLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="hover:text-accent-400 transition-colors duration-300"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-accent-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-600/3 rounded-full blur-3xl"></div>
    </footer>
  );
};

export default UpdatedFooter;